/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render the guardian-binding dialog, its request queue and in-person QR flow.
 * @state-owner dataset store holds guardians and requests; classroom UI store holds the channel
 * @allowed-imports React; dataset and workspace stores; classroom store; shared components and hooks
 * @forbidden direct API client; real QR encoding
 * @public-api classrooms module
 * @side-effects guardian record updates and toasts
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { ModalDocument, SectionLabel } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { QrCode } from '../../../shared/components/QrCode';
import { StatusText } from '../../../shared/components/StatusIcon';
import { CopyIcon, SmallCloseIcon } from '../../../shared/components/VispoIcon';
import type { GuardianChannel } from '../../../shared/lib/model';
import { guardianCode, guardianLink } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import {
  CHANNEL_COLOR,
  CHANNEL_LABEL,
  CHANNEL_NOTE,
  GUARDIAN_MAX,
  GUARDIAN_SOURCE,
  GUARDIAN_STATE_LABEL,
  guardianState,
} from '../model/guardian';
import { useClassroomUiStore } from '../store/classroom-ui-store';

const CHANNELS: readonly GuardianChannel[] = ['line', 'wa', 'wechat', 'sms'];

export function ChannelBadge({ channel }: { channel: GuardianChannel }) {
  const t = useTranslator();
  return (
    <span className="chb" style={{ ['--c' as string]: CHANNEL_COLOR[channel] }}>
      {t(CHANNEL_LABEL[channel])}
    </span>
  );
}

export function GuardianDialog({ student }: { student: string }) {
  const t = useTranslator();
  const { openModal } = useOverlay();
  const notify = useWorkspaceUiStore((state) => state.notify);
  const guardians = useDatasetStore((state) => state.guardians[student] ?? []);
  const requests = useDatasetStore((state) => state.guardianRequests);
  const roster = useDatasetStore((state) => state.roster);
  const channel = useClassroomUiStore((state) => state.guardianChannel);
  const setChannel = useClassroomUiStore((state) => state.setGuardianChannel);
  const state = guardianState(guardians);
  const url = guardianLink(student);
  const linkedCount = guardians.filter((record) => record.state === 'linked').length;
  const own = requests
    .map((request, index) => ({ ...request, index }))
    .filter((request) => request.student === student);

  const invite = () => {
    useDatasetStore
      .getState()
      .setGuardians(student, [
        ...guardians,
        { relation: t('家長'), channel, address: t('等待綁定'), state: 'pending' },
      ]);
    notify(t('已產生家長邀請，請把連結或 QR 給家長'));
  };

  const simulateRequest = () => {
    const entry = roster[student];
    const match = Math.random() > 0.5 ? 'match' : 'nomatch';
    useDatasetStore.getState().addGuardianRequest({
      student,
      source: 'link',
      name: entry?.relation ?? t('媽媽'),
      relation: entry?.relation ?? t('媽媽'),
      address: match === 'match' ? (entry?.phone ?? '09xx-xxx-xxx') : '09xx-xxx-888',
      match,
      channel,
      timeLabel: t('剛剛'),
    });
    notify(t('有一筆家長綁定請求等你確認'));
  };

  const resolveRequest = (index: number, approve: boolean) => {
    const store = useDatasetStore.getState();
    const request = store.guardianRequests[index];
    if (!request) return;
    if (approve) {
      store.setGuardians(request.student, [
        ...(store.guardians[request.student] ?? []),
        {
          relation: request.name || t('家長'),
          channel: request.channel,
          address: request.address || t('已驗證'),
          state: 'linked',
          source: request.source,
        },
      ]);
    }
    store.removeGuardianRequest(index);
    notify(approve ? t('已通過，這位家長現在收得到報告') : t('已拒絕這筆請求'));
  };

  return (
    <ModalDocument
      footer={
        <>
          <button
            className="bt"
            disabled={linkedCount >= GUARDIAN_MAX}
            type="button"
            onClick={invite}
          >
            {`${t('用')} ${t(CHANNEL_LABEL[channel])} ${t('送出邀請')}`}
          </button>
          <div className="sp" />
          <CloseButton />
        </>
      }
      maxWidth={520}
      subtitle={`${student} · ${t(GUARDIAN_STATE_LABEL[state][0])}`}
      title={t('家長綁定')}
    >
      <div className="gexp">
        {t(
          '報告只會寄給已綁定的家長。家長掃這個 QR 或開連結，確認是這位學生的家長之後就完成綁定。',
        )}
        <br />
        <b>{t('連結是同一組，管道只是把它送出去的方式。')}</b>
        <br />
        {t('同一組碼最多四位家長各自使用，綁定後所有班級都適用，不用重綁。')}
        <br />
        <b>{t('當面出示給家長掃是最可靠的，因為你看得到是誰掃的，這種不用再審核。')}</b>
      </div>
      <div className="gcode">
        <span>{t('綁定碼')}</span>
        <b>{guardianCode(student)}</b>
        <div className="sp" />
        <button
          className="lk"
          type="button"
          onClick={() => openModal(<GuardianPresentDialog student={student} />)}
        >
          {t('當面出示')}
        </button>
        <button className="lk" type="button" onClick={simulateRequest}>
          {t('模擬公開連結來的請求')}
        </button>
      </div>
      <div className="vqrbox" style={{ marginTop: 14 }}>
        <div className="vqrbrand">
          <span className="vqrlogo">V</span>
          {`${student} ${t('的家長')}`}
        </div>
        <div className="vqrimg">
          <QrCode seed={url} />
        </div>
      </div>
      <div className="vqrurl">
        <input readOnly value={url} />
        <button
          className="uib"
          title={t('複製連結')}
          type="button"
          onClick={() => notify(t('已複製連結'))}
        >
          <CopyIcon />
        </button>
      </div>
      <SectionLabel>{t('用哪個管道送給家長')}</SectionLabel>
      <div className="chrow">
        {CHANNELS.map((item) => (
          <button
            className={`chb2 ${channel === item ? 'on' : ''}`}
            key={item}
            style={{ ['--c' as string]: CHANNEL_COLOR[item] }}
            type="button"
            onClick={() => setChannel(item)}
          >
            {t(CHANNEL_LABEL[item])}
          </button>
        ))}
      </div>
      <div className="chnote">{t(CHANNEL_NOTE[channel])}</div>

      {own.length ? (
        <>
          <SectionLabel>{t('等你確認的請求')}</SectionLabel>
          <div className="greqs">
            {own.map((request) => (
              <div className="greq" key={`${request.student}-${request.index}`}>
                <div className="greqh">
                  <span className="gav" style={{ background: '#fef4e6', color: '#a15c07' }}>
                    ?
                  </span>
                  <span className="gt">
                    <b>{`${request.name}${t('（自己填的）')}`}</b>
                    <span>
                      <ChannelBadge channel={request.channel} />
                      {` ${request.address} · ${request.timeLabel}`}
                    </span>
                  </span>
                </div>
                <div className="greqm">
                  <div className="gmr">
                    <span>{t('來源')}</span>
                    <b>{t(GUARDIAN_SOURCE[request.source][0])}</b>
                    <em>{t(GUARDIAN_SOURCE[request.source][1])}</em>
                  </div>
                  <div className={`gmr ${request.match === 'match' ? 'ok' : 'no'}`}>
                    <span>{t('與報名表比對')}</span>
                    <b>{request.match === 'match' ? t('號碼相符') : t('號碼不符')}</b>
                    <em>
                      {request.match === 'match'
                        ? t('報名表上的家長號碼就是這一支')
                        : t('報名表上不是這支號碼，可能換號也可能不是本人')}
                    </em>
                  </div>
                </div>
                <div className="greqb">
                  <button
                    className="bt sm"
                    type="button"
                    onClick={() => notify(t('已在學生端問他這是不是他的家長'))}
                  >
                    {t('問學生')}
                  </button>
                  <div className="sp" />
                  <button
                    className="bt sm"
                    type="button"
                    onClick={() => resolveRequest(request.index, false)}
                  >
                    {t('拒絕')}
                  </button>
                  <button
                    className="bt sm pri"
                    type="button"
                    onClick={() => resolveRequest(request.index, true)}
                  >
                    {t('通過')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      <SectionLabel>
        {t('已連結的家長')}
        <span className="vmut" style={{ fontWeight: 400, marginLeft: 6 }}>
          {`${linkedCount} / ${GUARDIAN_MAX}`}
        </span>
      </SectionLabel>
      {guardians.length ? (
        <div className="glist">
          {guardians.map((record, index) => (
            <div className="vgrow" key={`${record.relation}-${record.address}`}>
              <span className="gav">{record.relation[0]}</span>
              <span className="gt">
                <b>{record.relation}</b>
                <span>
                  <ChannelBadge channel={record.channel} />
                  {` ${record.address}${record.source ? ` · ${t(GUARDIAN_SOURCE[record.source][0])}` : ''}`}
                </span>
              </span>
              {record.state === 'linked' ? (
                <StatusText glyph="done" label={t('已綁定')} />
              ) : (
                <StatusText glyph="half" label={t('待確認')} />
              )}
              {record.state === 'pending' ? (
                <button
                  className="bt sm"
                  type="button"
                  onClick={() => {
                    useDatasetStore.getState().setGuardians(
                      student,
                      guardians.map((item, position) =>
                        position === index ? { ...item, state: 'linked' as const } : item,
                      ),
                    );
                    notify(t('家長已完成綁定'));
                  }}
                >
                  {t('模擬家長完成綁定')}
                </button>
              ) : null}
              <button
                className="uib"
                title={t('移除')}
                type="button"
                onClick={() => {
                  useDatasetStore.getState().setGuardians(
                    student,
                    guardians.filter((_, position) => position !== index),
                  );
                  notify(t('已移除'));
                }}
              >
                <SmallCloseIcon />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="vguide" style={{ marginTop: 2 }}>
          <b>{t('還沒有家長綁定')}</b>
          <span>{t('這位學生的報告目前沒有人收得到。')}</span>
        </div>
      )}
    </ModalDocument>
  );
}

function CloseButton() {
  const t = useTranslator();
  const { closeModal } = useOverlay();
  return (
    <button className="bt pri" type="button" onClick={closeModal}>
      {t('完成')}
    </button>
  );
}

export function GuardianPresentDialog({ student }: { student: string }) {
  const t = useTranslator();
  const { closeModal, openModal } = useOverlay();
  const notify = useWorkspaceUiStore((state) => state.notify);
  const classes = useDatasetStore((state) => state.classes);
  const roster = useDatasetStore((state) => state.roster);
  const channel = useClassroomUiStore((state) => state.guardianChannel);

  return (
    <div className="rdoc gpres">
      <div className="rhd">
        <button className="cls5" type="button" onClick={closeModal}>
          ×
        </button>
        <h2>{t('請家長掃這個 QR')}</h2>
        <p>{`${student} · ${classes[0].name}`}</p>
      </div>
      <div className="rbody" style={{ textAlign: 'center' }}>
        <div className="gpq">
          <QrCode seed={guardianLink(student)} />
        </div>
        <div className="gpc">{guardianCode(student)}</div>
        <div className="gpn">
          {t('你現在是當面出示，所以掃進來的綁定會直接成立，不用再審核。')}
          <br />
          {t('離開這個畫面之後掃進來的就會變成待核准。')}
        </div>
      </div>
      <div className="rfoot">
        <button className="bt" type="button" onClick={closeModal}>
          {t('關閉')}
        </button>
        <div className="sp" />
        <button
          className="bt pri"
          type="button"
          onClick={() => {
            const store = useDatasetStore.getState();
            const entry = roster[student];
            store.setGuardians(student, [
              ...(store.guardians[student] ?? []),
              {
                relation: entry?.relation ?? t('媽媽'),
                channel,
                address: entry?.phone ?? '09xx-xxx-xxx',
                state: 'linked',
                source: 'teacher',
              },
            ]);
            openModal(<GuardianDialog student={student} />);
            notify(t('當面掃的，直接綁定完成，不用審核'));
          }}
        >
          {t('模擬家長掃了')}
        </button>
      </div>
    </div>
  );
}
